import type { WorkExperienceCompany, WorkExperienceRole } from '@applicator/shared'

/** Company block visible when master company checkbox is on and at least one role shows in preview. */
export function isWorkExperienceCompanyInPreview(company: WorkExperienceCompany): boolean {
  if (!company.included) return false
  return (company.roles || []).some((role) => isWorkExperienceRoleInPreview(company, role))
}

/**
 * Role visible when company is on and the position master checkbox is on
 * (or only included bullets remain with position off).
 * Aligns with editor: handleTogglePosition / positionIncluded, not legacy role.included alone.
 */
export function isWorkExperienceRoleInPreview(
  company: WorkExperienceCompany,
  role: WorkExperienceRole
): boolean {
  if (!company.included) return false

  const positionOn = role.positionIncluded ?? true
  const hasIncludedBullets = (role.bulletPoints || []).some((b) => b.included)

  if (!positionOn) {
    return hasIncludedBullets
  }

  const positionHasText = Boolean(role.position?.trim())
  const locationOn = (role.locationIncluded ?? true) && Boolean(role.location?.trim())
  return positionHasText || locationOn || hasIncludedBullets
}

/** Company name + description on a role block in preview (same gate as role visibility). */
export function isWorkExperienceCompanyHeaderInRolePreview(
  company: WorkExperienceCompany,
  role: WorkExperienceRole
): boolean {
  if (!isWorkExperienceRoleInPreview(company, role)) return false
  const nameOn = (company.nameIncluded ?? true) && Boolean(company.name?.trim())
  const descOn = (company.descriptionIncluded ?? true) && Boolean(company.description?.trim())
  return nameOn || descOn
}

/** First visible role for a company (Teal shows company description once, before position). */
export function isFirstWorkExperienceRoleInCompanyPreview(
  company: WorkExperienceCompany,
  role: WorkExperienceRole
): boolean {
  const roles = (company.roles || []).filter((r) => isWorkExperienceRoleInPreview(company, r))
  return roles.length > 0 && roles[0]!.id === role.id
}

export function isWorkExperienceCompanyDescriptionInPreview(
  company: WorkExperienceCompany,
  role: WorkExperienceRole
): boolean {
  if (!isFirstWorkExperienceRoleInCompanyPreview(company, role)) return false
  return (company.descriptionIncluded ?? true) && Boolean(company.description?.trim())
}
